import { useEffect, useRef } from 'react'
import useFilterNotifications from './useFilterNotifications'


function TabMatchTracker({ tabId, isActive, onNewMatches }) {
	const { notifications, loading } = useFilterNotifications(tabId)
	const prevCountRef = useRef(0)

	useEffect(() => {
		// Skip if still loading
		if (loading) {
			return
		}

		// IMPORTANT: Only track notifications for INACTIVE tabs
		// Active tab doesn't need notifications (user is looking at it)
		if (isActive) {
			// Reset count when tab becomes active
			prevCountRef.current = notifications.length
			return
		}

		const currentCount = notifications.length

		// Track NEW notifications (count increased since last check)
		const newCount = Math.max(0, currentCount - prevCountRef.current)


		// Update ref for next comparison
		prevCountRef.current = currentCount

		// If there are new notifications, report them to parent
		if (newCount > 0) {
			// Get load IDs from the NEW notifications
			// Format: "source:loadId" to match highlighting logic
			const newNotifications = notifications.slice(-newCount)


			const loadIds = newNotifications
				.filter(n => n.load)
				.map(n => {
					const id = `${n.load.source || ''}:${n.load.loadId}`
					return id
				})


			// Pass both loadIds and full notification data for popup display
			onNewMatches(tabId, loadIds, isActive, currentCount, newNotifications)
		}
	}, [notifications, loading, tabId, isActive, onNewMatches])

	// Debug (dev only): log new matches to the console (all tabs).
	const loggedCountRef = useRef(null)
	useEffect(() => {
		if (loading || process.env.NODE_ENV === 'production') return
		const count = notifications.length
		if (loggedCountRef.current === null) {
			if (count > 0) {
				console.log(`🔔 [notifications] tab ${tabId}: ${count} existing unread match(es)`)
			}
			loggedCountRef.current = count
			return
		}
		const delta = count - loggedCountRef.current
		loggedCountRef.current = count
		if (delta > 0) {
			const fresh = notifications.slice(0, delta).map(n =>
				n.load
					? `${n.load.originCity || '?'}, ${n.load.originState || '?'} → ${n.load.deliveryCity || '?'}, ${n.load.deliveryState || '?'}${n.load.price ? ` ($${n.load.price})` : ''}`
					: n._id,
			)
			console.log(`🔔 [notifications] +${delta} NEW match(es) on tab ${tabId} (unread total ${count})`, fresh)
		}
	}, [notifications, loading, tabId])

	// Invisible component
	return null
}

export default TabMatchTracker
